"use client";

import { useEffect } from "react";
import { useAtomValue } from "jotai";
import {
  nodeDataMapAtom,
  edgesAtom,
  nodeRegistryAtom,
  dynamicNodeConfigsAtom,
  activeFlowIdAtom,
  selectedNodeIdAtom,
} from "@/store/editor-v2";
import { useSpotUser } from "@/utils/spotUser";
import { useSecretsSources } from "@/components/SpotflowV2/Sidebar/useSecretsSources";
import { syncEditorStore, disposeInputs } from "./inputsStore";
import type { EditorInput } from "./types";

// Feeds the per-editor inputsStore that chip components read: the field's
// reachable inputs plus the flow snapshot used to resolve stale/unknown refs.
export function useEditorStoreSync(
  editorId: string,
  inputs: EditorInput[],
  nodeId?: string,
  variableBindings?: Record<string, string | undefined>
) {
  const selectedNodeId = useAtomValue(selectedNodeIdAtom);
  const nodeDataMap = useAtomValue(nodeDataMapAtom);
  const edges = useAtomValue(edgesAtom);
  const nodeRegistry = useAtomValue(nodeRegistryAtom);
  const dynamicNodeConfigs = useAtomValue(dynamicNodeConfigsAtom);
  const activeFlowId = useAtomValue(activeFlowIdAtom);
  const { spotGroup } = useSpotUser();
  const { secrets } = useSecretsSources(spotGroup?.id, activeFlowId);
  const resolvedNodeId = nodeId ?? selectedNodeId ?? undefined;

  useEffect(() => {
    if (resolvedNodeId) {
      syncEditorStore(editorId, {
        inputs,
        resolveContext: {
          nodeId: resolvedNodeId,
          variableBindings,
        },
        flowSnapshot: {
          nodeId: resolvedNodeId,
          nodeDataMap,
          edges,
          nodeRegistry,
          dynamicNodeConfigs,
          secrets,
          variableBindings,
        },
      });
    } else {
      syncEditorStore(editorId, {
        inputs,
        resolveContext: null,
        flowSnapshot: null,
      });
    }
  }, [
    editorId,
    inputs,
    resolvedNodeId,
    variableBindings,
    nodeDataMap,
    edges,
    nodeRegistry,
    dynamicNodeConfigs,
    secrets,
  ]);

  useEffect(
    () => () => {
      disposeInputs(editorId);
    },
    [editorId]
  );
}
